/**
 * FormMetaContext — bridges each form's load-time + Audit metadata from the
 * shared <FormTitle> (which EVERY form already renders) to the shared
 * <AppToolbar>, so the "Loaded in X sec" chip + the view-only Audit chip render
 * at the FAR RIGHT of the action bar, next to "Export to" — on every form, with
 * ZERO per-form change.
 *
 * (BO UI Standard v3, user request 2026-09-18: "if there's a Loaded in X sec it
 * must come next to Export; if BOTH Loaded and Audit are there show both next to
 * Export, for all forms." The page title now lives in the top bar and the
 * on-page title row is dropped, so the chips have nowhere to sit on the title
 * row anyway — this puts them where the user asked.)
 *
 * HOW IT WORKS
 *   - <FormTitle>  calls usePublishFormMeta({loadMs, loading, audit…}) →
 *     publishes to the context and renders nothing itself when a toolbar exists.
 *   - <AppToolbar> calls useRegisterToolbar() (so FormTitle knows a toolbar is
 *     present) and renders the chips from useFormMeta() after "Export to".
 *   - A form with NO <AppToolbar> keeps its chips in <FormTitle> (fallback), so
 *     the load-time is never lost.
 *
 * DESIGN — three SEPARATE contexts so the actions object is STABLE for the life
 * of the provider (memoized with [] deps; useState setters are stable). If the
 * actions were bundled with the changing `meta`/`hasToolbar` into one value, that
 * value's identity would change on every publish, and any effect keyed on it
 * would re-run its cleanup and clear the meta on every render (the chip would
 * never show). Splitting keeps publish/register effects from thrashing.
 * Mounted once at the app root (main.tsx), above <App/>.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

export interface FormMeta {
  /** Last fetch duration (ms) — drives the shared LoadTimeChip. */
  loadMs?: number | null;
  /** True while fetching — the chip shows "Loading…". */
  loading?: boolean;
  /** Audit Trail (view-only) — config-gated chip; matches dbo.AuditData.FormName. */
  auditFormName?: string;
  auditSourceId?: string;
}

interface FormMetaActions {
  setMeta: (m: FormMeta) => void;
  registerToolbar: (present: boolean) => void;
}

/** Changes only when the current form's meta changes (consumers: AppToolbar). */
const MetaValueCtx = createContext<FormMeta>({});
/** Changes only when a toolbar mounts/unmounts (consumers: FormTitle). */
const HasToolbarCtx = createContext<boolean>(false);
/** STABLE for the provider's life — publishers/registrars key their effects here. */
const ActionsCtx = createContext<FormMetaActions | null>(null);

export function FormMetaProvider({ children }: { children: ReactNode }) {
  const [meta, setMeta] = useState<FormMeta>({});
  const [toolbarCount, setToolbarCount] = useState(0);
  // Stable actions — setMeta / setToolbarCount identities never change, so this
  // object is created once and never invalidates a consumer's effect deps.
  const actions = useMemo<FormMetaActions>(() => ({
    setMeta,
    registerToolbar: (present: boolean) =>
      setToolbarCount((n) => Math.max(0, n + (present ? 1 : -1))),
  }), []);
  return (
    <ActionsCtx.Provider value={actions}>
      <HasToolbarCtx.Provider value={toolbarCount > 0}>
        <MetaValueCtx.Provider value={meta}>{children}</MetaValueCtx.Provider>
      </HasToolbarCtx.Provider>
    </ActionsCtx.Provider>
  );
}

/** <FormTitle> publishes its load-time + audit here so <AppToolbar> can show them. */
export function usePublishFormMeta(meta: FormMeta): void {
  const actions = useContext(ActionsCtx);
  const { loadMs, loading, auditFormName, auditSourceId } = meta;
  useEffect(() => {
    actions?.setMeta({ loadMs, loading, auditFormName, auditSourceId });
  }, [actions, loadMs, loading, auditFormName, auditSourceId]);
  // Clear on unmount (actions is stable, so this runs ONLY on real unmount) so
  // the next page — which may have no FormTitle — shows no stale chip.
  useEffect(() => () => actions?.setMeta({}), [actions]);
}

/** <AppToolbar> registers its presence (useLayoutEffect = before paint, so
 *  FormTitle defers its chips to the toolbar with no visible flash). */
export function useRegisterToolbar(): void {
  const actions = useContext(ActionsCtx);
  useLayoutEffect(() => {
    actions?.registerToolbar(true);
    return () => actions?.registerToolbar(false);
  }, [actions]);
}

/** Read the current form's load-time + audit (for <AppToolbar>). */
export function useFormMeta(): FormMeta {
  return useContext(MetaValueCtx);
}

/** True when an <AppToolbar> is mounted for the current form (for <FormTitle>). */
export function useHasToolbar(): boolean {
  return useContext(HasToolbarCtx);
}
